import Divider from '@material-ui/core/Divider'
import classNames from 'classnames'
import React, { FC, memo } from 'react'
import { Column, ColumnType } from '../../models/column'
import Cell from './Cell'
import { makeStyles } from '@material-ui/core'
import { stores } from '@stores'
import { View } from '@modules/order/constants/views'

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    backgroundColor: 'white',
    fontSize: '12px'
  },
  border: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    zIndex: 10
  },
  groupedBorder: {
    zIndex: 9
  },
  containerBorder: {
    borderRight: '1px solid #ededed'
  },
  containerTopBorder: {
    borderTop: '1px solid #0000001f'
  },
  groupedHeaderContainer: {
    display: 'flex',
    flexDirection: 'column',
    '&.groupedHeaderContainerOverride:last-of-type .groupedHeaderColumnsOverride .customBorderOverride:after':
      {
        borderRight: '0px'
      }
  },
  evenOddBackgroundHeader: {
    '&:nth-child(odd)': {
      backgroundColor: '#cccccc'
    },
    '&:nth-child(even)': {
      backgroundColor: '#dddddd'
    }
  },
  groupedHeaderCell: {
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 500,
    position: 'relative',
    marginLeft: '-1px'
  },
  groupedHeaderColumns: {
    flex: 1
  },
  enhancedLeftBorder: {
    '& > div': {
      borderLeft: '1px solid #ccc'
    }
  }
}))

export interface HeaderProps {
  columns: Column[]
  showBorders?: boolean
  enhancedViewCell?: boolean
}

interface PrivateHeaderProps {
  stickyColsPosition?: { [field: string]: number }
}

const Header: FC<HeaderProps & PrivateHeaderProps> = ({
  columns,
  stickyColsPosition,
  showBorders,
  enhancedViewCell
}) => {
  const classes = useStyles()
  const {
    nav: {
      queryParams: { view }
    },
    productConfiguration: { enhancedViewEnabled }
  } = stores

  return (
    <div
      className={classNames(classes.container, {
        [classes.containerBorder]: showBorders && !enhancedViewEnabled,
        [classes.containerTopBorder]: enhancedViewCell
      })}
    >
      {columns?.map(column =>
        column.type === ColumnType.Grouped ? (
          <div
            key={column.field}
            className={classNames(
              classes.groupedHeaderContainer,
              'groupedHeaderContainerOverride',
              { [classes.evenOddBackgroundHeader]: view === View.KPI }
            )}
          >
            <div
              className={classNames(classes.groupedHeaderCell, classes.groupedBorder, {
                [classes.enhancedLeftBorder]:
                  enhancedViewEnabled &&
                  !enhancedViewCell &&
                  column?.showBorders &&
                  !column?.isStickyBorder
              })}
            >
              <Cell column={column} noCellBackground={view === View.KPI} />
              {column.hideDivider ? null : (
                <Divider variant='fullWidth' className={classes.border} />
              )}
            </div>
            <div
              className={classNames(
                classes.groupedHeaderColumns,
                'groupedHeaderColumnsOverride'
              )}
            >
              <Header columns={column.columns} />
            </div>
          </div>
        ) : column.type === ColumnType.Inline ? (
          <Cell
            key={column.field}
            column={column}
            stickyPosition={stickyColsPosition?.[column.field]}
          />
        ) : (
          <Cell
            key={column.field}
            column={column}
            stickyPosition={stickyColsPosition?.[column.field]}
            enhancedViewCell={enhancedViewCell}
          />
        )
      )}
      {showBorders && <Divider variant='fullWidth' className={classes.border} />}
    </div>
  )
}

export default memo(Header)
